/** Base NAS expedientes (misma ruta que backend / Access). */
export const NAS_EXPEDIENTES_BASE =
  "\\\\vitalcasa\\vitalcasagestion\\Documentacion\\Documentacion Expedientes\\2026";

/** Normaliza ruta UNC de expediente (siempre backslashes). */
export function normalizarRutaUnc(ruta) {
  return String(ruta || "").trim().replace(/\//g, "\\");
}

/** PR01/26 → \\vitalcasa\...\2026\PR01-26  |  01/26 → ...\01-26 */
export function rutaCarpetaDesdeNumero(numero) {
  const num = String(numero || "").trim();
  if (!num) return "";
  const nombre = num.replace(/\//g, "-").replace(/[<>:"|?*]/g, "-");
  return `${NAS_EXPEDIENTES_BASE}\\${nombre}`;
}

const HELPER_URL = "http://127.0.0.1:47829";
const HELPER_TIMEOUT_MS = 3000;
const HELPER_MIN_VERSION = 2;
const HELPER_SYNC_MIN_VERSION = 3;

/** Mensaje estándar: una instalación por PC de la oficina. */
export const NAS_HELPER_INSTALAR_MSG =
  "En cada PC de la oficina hay que instalar el conector NAS una sola vez:\n\n" +
  "Clic derecho → Ejecutar con PowerShell:\n" +
  "backend\\scripts\\instalar-abridor-nas.ps1\n\n" +
  "(Desde la carpeta del proyecto en ese ordenador. Repetir en todos los que usen Gestión Ventas.)";

async function helperHealth() {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), 800);
  try {
    const resp = await fetch(`${HELPER_URL}/health`, { mode: "cors", signal: ctrl.signal });
    clearTimeout(timer);
    if (!resp.ok) return null;
    return await resp.json().catch(() => null);
  } catch {
    clearTimeout(timer);
    return null;
  }
}

async function helperDisponible() {
  const data = await helperHealth();
  return !!data?.ok;
}

/** Helper con guardado /save (v2+). */
async function helperGuardadoDisponible() {
  const data = await helperHealth();
  if (!data?.ok) return { ok: false, needsInstall: true };
  const version = Number(data.version) || 1;
  const saveOk = data.save === true || version >= HELPER_MIN_VERSION;
  if (!saveOk) {
    return { ok: false, needsReinstall: true, error: "Helper desactualizado (falta guardado en NAS)" };
  }
  return { ok: true, version };
}

async function helperSyncDisponible() {
  const cap = await helperGuardadoDisponible();
  if (!cap.ok) return cap;
  const data = await helperHealth();
  const version = Number(data?.version) || 1;
  if (data?.syncDocs === true || version >= HELPER_SYNC_MIN_VERSION) {
    return { ok: true, version };
  }
  return { ok: false, needsReinstall: true, error: "Helper desactualizado (falta sincronizar documentos)" };
}

export async function estadoHelperNas() {
  const data = await helperHealth();
  if (!data?.ok) return { instalado: false, version: 0, save: false, syncDocs: false };
  return {
    instalado: true,
    version: Number(data.version) || 1,
    save: !!data.save,
    syncDocs: !!data.syncDocs,
  };
}

async function tryHelperLocal(ruta) {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), HELPER_TIMEOUT_MS);
  try {
    const resp = await fetch(`${HELPER_URL}/open`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ruta }),
      mode: "cors",
      signal: ctrl.signal,
    });
    clearTimeout(timer);
    if (!resp.ok) return false;
    const data = await resp.json().catch(() => ({}));
    return data.ok === true;
  } catch {
    clearTimeout(timer);
    return false;
  }
}

/** Protocolo vitalcasa-nas:// — Windows lanza WScript en la sesión del usuario. */
function tryProtocoloLocal(ruta) {
  try {
    const href = `vitalcasa-nas://open?p=${encodeURIComponent(ruta)}`;
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.src = href;
    document.body.appendChild(iframe);
    setTimeout(() => iframe.remove(), 3000);
    const a = document.createElement("a");
    a.href = href;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } catch { /* */ }
}

/**
 * Abre carpeta NAS en el Explorador de Windows (como Access).
 * Requiere instalar una vez: backend/scripts/instalar-abridor-nas.ps1
 */
export async function abrirCarpetaExpedienteEnWindows(ruta) {
  const normalized = normalizarRutaUnc(ruta);
  if (!normalized) return { ok: false };

  // Protocolo + helper en paralelo (máxima compatibilidad)
  tryProtocoloLocal(normalized);

  if (await helperDisponible()) {
    if (await tryHelperLocal(normalized)) {
      return { ok: true, method: "helper" };
    }
  }

  await new Promise((r) => setTimeout(r, 400));
  if (await helperDisponible() && await tryHelperLocal(normalized)) {
    return { ok: true, method: "helper-retry" };
  }

  let clipboardOk = false;
  try {
    await navigator.clipboard.writeText(normalized);
    clipboardOk = true;
  } catch { /* */ }

  return { ok: false, needsInstall: true, clipboardOk };
}

/** Guarda un archivo en NAS vía helper local (Windows). */
export async function guardarArchivoEnNasLocal(carpeta, nombreArchivo, blob) {
  const normalized = normalizarRutaUnc(carpeta);
  const nombre = String(nombreArchivo || "").trim();
  if (!normalized || !nombre) return { ok: false, error: "ruta_invalida" };

  const cap = await helperGuardadoDisponible();
  if (!cap.ok) {
    return {
      ok: false,
      needsInstall: !!cap.needsInstall,
      needsReinstall: !!cap.needsReinstall,
      error: cap.error || (cap.needsInstall ? "helper_no_instalado" : "helper_desactualizado"),
    };
  }

  let contentBase64;
  if (typeof blob === "string") {
    contentBase64 = blob;
  } else {
    contentBase64 = await blobToBase64(blob);
  }
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), 60000);
  try {
    const resp = await fetch(`${HELPER_URL}/save`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ carpeta: normalized, nombre, contentBase64 }),
      mode: "cors",
      signal: ctrl.signal,
    });
    clearTimeout(timer);
    const data = await resp.json().catch(() => ({}));
    if (resp.status === 404 || data.error === "Not found") {
      return {
        ok: false,
        needsReinstall: true,
        error: "Helper desactualizado. Vuelve a ejecutar instalar-abridor-nas.ps1",
      };
    }
    if (!resp.ok || !data.ok) {
      return { ok: false, error: data.error || `HTTP ${resp.status}` };
    }
    return { ok: true, ruta: data.ruta, method: "helper-save" };
  } catch (e) {
    clearTimeout(timer);
    return { ok: false, error: e.message || "helper_save_failed" };
  }
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      const base64 = String(dataUrl || "").split(",")[1] || "";
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function mimeContratoDesdeNombre(nombre) {
  if (/\.pdf$/i.test(String(nombre || ""))) return "application/pdf";
  return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
}

export function base64ToDocxBlob(base64, nombre = "") {
  const raw = String(base64 || "").trim();
  if (!raw) return null;
  const bin = atob(raw);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mimeContratoDesdeNombre(nombre) });
}

function descargarBlob(blob, nombre) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Guarda en NAS (helper local) y solo descarga si no fue posible guardar allí.
 * @returns {{ guardadoNas: boolean, rutaNas: string, errorNas: string, descargado: boolean }}
 */
export async function guardarContratoEnNasPreferido({ carpeta, nombre, blob, base64 }) {
  const fileBlob = blob || (base64 ? base64ToDocxBlob(base64, nombre) : null);
  if (!fileBlob) return { guardadoNas: false, rutaNas: "", errorNas: "sin_archivo", descargado: false };

  let rutaNas = "";
  let local = await guardarArchivoEnNasLocal(carpeta, nombre, fileBlob);
  if (!local.ok) {
    await new Promise((r) => setTimeout(r, 400));
    local = await guardarArchivoEnNasLocal(carpeta, nombre, fileBlob);
  }
  if (local.ok) {
    return { guardadoNas: true, rutaNas: local.ruta || "", errorNas: "", descargado: false, method: local.method };
  }

  descargarBlob(fileBlob, nombre);
  const errMsg = local.error
    || (local.needsReinstall ? "Helper desactualizado. Vuelve a ejecutar instalar-abridor-nas.ps1" : "")
    || (local.needsInstall ? "helper_no_instalado" : "helper_save_failed");
  return {
    guardadoNas: false,
    rutaNas: "",
    errorNas: errMsg,
    descargado: true,
    needsInstall: !!local.needsInstall,
    needsReinstall: !!local.needsReinstall,
  };
}

/** Crea carpeta y copia documentos de propiedad al NAS (helper v3, cada PC de la oficina). */
export async function sincronizarDocumentosNasLocal(carpeta, documentos) {
  const normalized = normalizarRutaUnc(carpeta);
  if (!normalized) return { ok: false, error: "ruta_invalida" };

  const cap = await helperSyncDisponible();
  if (!cap.ok) {
    return {
      ok: false,
      needsInstall: !!cap.needsInstall,
      needsReinstall: !!cap.needsReinstall,
      error: cap.error || (cap.needsInstall ? "helper_no_instalado" : "helper_desactualizado"),
    };
  }

  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), 180000);
  try {
    const resp = await fetch(`${HELPER_URL}/sync-docs`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ carpeta: normalized, documentos: documentos || [] }),
      mode: "cors",
      signal: ctrl.signal,
    });
    clearTimeout(timer);
    const data = await resp.json().catch(() => ({}));
    if (resp.status === 404 || data.error === "Not found") {
      return { ok: false, needsReinstall: true, error: "Helper desactualizado" };
    }
    if (!resp.ok || !data.ok) {
      return { ok: false, error: data.error || `HTTP ${resp.status}` };
    }
    return {
      ok: true,
      carpeta: data.carpeta || normalized,
      copiados: data.copiados || 0,
      omitidos: data.omitidos || 0,
      errores: data.errores || [],
    };
  } catch (e) {
    clearTimeout(timer);
    return { ok: false, error: e.message || "helper_sync_failed" };
  }
}

/** Pide al API la lista de docs y los sincroniza en el NAS de este PC. */
export async function sincronizarExpedienteNasDesdeApi(api, expedienteId) {
  if (!expedienteId || !api) return { ok: false, error: "sin_expediente" };
  try {
    const r = await api.get(`/expedientes/${expedienteId}/documentos-para-nas`);
    const { carpetaNas, documentos } = r.data?.data || {};
    if (!carpetaNas) return { ok: false, error: "sin_carpeta" };
    if (!documentos?.length) return { ok: true, copiados: 0, omitidos: 0, sinDocumentos: true };
    return sincronizarDocumentosNasLocal(carpetaNas, documentos);
  } catch (e) {
    return { ok: false, error: e.response?.data?.error || e.message || "sync_api_failed" };
  }
}
